/*
 * Holds the active query scope — the (firmId, asOf) pair every read in the app is
 * answered under. The router writes it after each confirmed navigation; everything
 * else reads it.
 *
 * IMPORT RULE: this module and `@/trpc/queryClients` are imported by their own paths
 * (`@/trpc/scope`, `@/trpc/queryClients`), never through the `@/trpc` barrel, and the
 * barrel does not re-export them. Roughly 123 test files carry a
 * `vi.mock('@/trpc', () => ({ trpc: … }))` factory; anything reading `scopeRef` or
 * `getScope` through the barrel would be `undefined` under those mocks. This file
 * depends only on `vue` and `AppStorage`, so it runs for real under test — a test that
 * needs a particular scope calls `setScope(...)` directly.
 */

// library imports
import { computed, ref, type ComputedRef } from 'vue';

// local imports
import { AppStorage, StorageKey } from '@/utils/storage';

// types
import type { QueryScope } from '@/types/queryScope';

/*
 * Not seeded from AppStorage. Nothing needs a firm id before the router confirms one —
 * the only request that can fire before then is `users.getCurrent`, a publicProcedure
 * that ignores the firm header.
 */
const activeScope = ref<QueryScope>({ firmId: '', asOf: null });

/** The active scope, as a reactive computed (for templates and watchers). */
export const scopeRef: ComputedRef<QueryScope> = computed(() => activeScope.value);

/**
 * Reads the active query scope.
 * @returns the current (firmId, asOf) pair
 */
export function getScope(): QueryScope {
  return activeScope.value;
}

/**
 * Builds the stable string identity of a scope, used to key the QueryClient registry
 * and the provider component that swaps clients.
 * @param scope - the scope to identify
 * @returns `${firmId}|${asOf ?? ''}`
 */
export function scopeKey(scope: QueryScope): string {
  return `${scope.firmId}|${scope.asOf ?? ''}`;
}

/**
 * The only writer of the active scope. Callers are `activateScope` (the router's
 * transition) and the session-teardown paths in mainStore.
 *
 * The persisted active firm id follows the firm dimension only — a date-only change
 * leaves storage alone, because the stored value is the login-time firm preference,
 * not the scope.
 *
 * @param next - the scope to make active
 */
export function setScope(next: QueryScope): void {
  const previousFirmId = activeScope.value.firmId;

  activeScope.value = next;

  if (next.firmId === previousFirmId)
    return;

  if (next.firmId)
    AppStorage.set(StorageKey.ActiveFirmId, next.firmId);
  else
    AppStorage.remove(StorageKey.ActiveFirmId);
}
